/**
 * Pathway editor mode store (Sprint 4.3.2, PROD-651).
 *
 * Holds the per-session toggle between `Design Mode` (the canonical
 * pathway-authoring surface that has shipped through Sprint 4.3.1) and
 * `Runtime Mode` (the new orchestration view introduced by D4 RESOLVED:
 * single CPS app + Mode toggle, NOT a separate runtime service).
 *
 * Design Mode:
 *   - Default. Clicking an Action node opens the existing
 *     `PathwayInspector` (label / description / state-machine / lab
 *     basket / override).
 *   - Multiplicity badges hidden.
 *   - Replay timeline panel hidden.
 *
 * Runtime Mode:
 *   - Clicking an Action node opens the `ActivityInspector` overlay
 *     showing live activity state per Awell orchestration ontology
 *     (`activity_id`, status enum, source-trigger ref, start_date,
 *     completion_date, `indirect_object`).
 *   - Action nodes display a multiplicity pill ("Form: 3 activities")
 *     when N > 1 FHIR Tasks instantiate the action def.
 *   - Pathway timeline sub-panel becomes available (collapsible).
 *
 * Reference:
 *   - Awell orchestration ontology (vault):
 *     03-knowledge-base/external-references/awell/orchestration/docs/
 *     getting-started/orchestration-ontology.md
 *     ("When a care flow is orchestrated, it is translated to a set of
 *     activities. ... an activity is created in the Awell System to keep
 *     track of its start date, status, completion date, etc.")
 *
 * Persistence: localStorage under `cps:pathway-editor:mode` (per-session
 * + per-browser; deliberately not tenant-scoped because the mode is a
 * UI affordance for the operator viewing the editor, not a property of
 * the pathway being viewed).
 *
 * Telemetry (HC9 strict registry): every mode change emits an
 * AuditEvent-shaped `pathway-editor-mode-change` envelope via
 * `emitPathwayEditorModeChange`. The emit lives outside the store
 * setter so a hydrate from localStorage on mount does NOT spam the
 * sink with a synthetic "change".
 *
 * HC1 reproducibility: the store is in-memory + synchronous so toggle
 * latency is dominated by the React re-render cost. Tests inject a
 * deterministic mode by calling `usePathwayEditorModeStore.setState({
 * mode: 'runtime' })` before render.
 *
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: the consuming `<PathwayModeToggle>` is `role="radiogroup"`.
 */
import { create } from 'zustand';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';

export type PathwayEditorMode = 'design' | 'runtime';

export const PATHWAY_EDITOR_MODE_STORAGE_KEY = 'cps:pathway-editor:mode';

export interface PathwayEditorModeState {
  mode: PathwayEditorMode;
  /** Toggle to the other mode. Persists + emits telemetry. */
  toggle: () => void;
  /** Set mode explicitly. No-op when current. Persists + emits. */
  setMode: (next: PathwayEditorMode) => void;
  /**
   * Hydrate mode from localStorage. Called once at mount by the
   * `<PathwayModeToggle>` surface. Does NOT emit telemetry (the user
   * did not change anything; we are restoring prior state).
   */
  hydrate: () => void;
}

/**
 * Read the persisted mode. Returns `null` when no value is stored so
 * the hydrate path can distinguish "first mount, no prior selection"
 * (do not touch the in-memory mode) from "explicit prior selection
 * present in localStorage" (apply it).
 */
export function readPersistedPathwayEditorMode(): PathwayEditorMode | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PATHWAY_EDITOR_MODE_STORAGE_KEY);
    if (raw === 'runtime') return 'runtime';
    if (raw === 'design') return 'design';
    return null;
  } catch {
    return null;
  }
}

/** Persist the mode. Best-effort; failures log + swallow. */
function persistPathwayEditorMode(mode: PathwayEditorMode): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PATHWAY_EDITOR_MODE_STORAGE_KEY, mode);
  } catch (err) {
    console.warn(
      '[pathway-editor-mode] persist failed; mode will not survive reload',
      err,
    );
  }
}

/**
 * Emit the HC9 strict registry envelope for a mode change. The event
 * name is the fixed registry source `pathway-editor-mode-change` so
 * the dashboard can split this stream from the global `mode_switch`
 * (which models the Demo / Live runtime mode rather than the editor
 * surface mode).
 */
function emitPathwayEditorModeChange(args: {
  from_mode: PathwayEditorMode;
  to_mode: PathwayEditorMode;
}): void {
  emitTelemetry('pathway_editor_mode_change', {
    source: 'pathway-editor-mode-change',
    from_mode: args.from_mode,
    to_mode: args.to_mode,
    correlation_id: newCorrelationId(),
    ts: new Date().toISOString(),
  });
}

export const usePathwayEditorModeStore = create<PathwayEditorModeState>(
  (set, get) => ({
    // Default Design per task spec; localStorage hydrate flips to runtime
    // when persisted.
    mode: 'design' as PathwayEditorMode,
    toggle: () => {
      const previous = get().mode;
      const next: PathwayEditorMode =
        previous === 'design' ? 'runtime' : 'design';
      set({ mode: next });
      persistPathwayEditorMode(next);
      emitPathwayEditorModeChange({ from_mode: previous, to_mode: next });
    },
    setMode: (next) => {
      const previous = get().mode;
      if (previous === next) return;
      set({ mode: next });
      persistPathwayEditorMode(next);
      emitPathwayEditorModeChange({ from_mode: previous, to_mode: next });
    },
    hydrate: () => {
      const persisted = readPersistedPathwayEditorMode();
      // Only flip when localStorage carries an explicit prior selection.
      // No persisted value means the user has never toggled, so the
      // in-memory default (or test-injected mode) wins.
      if (persisted !== null && get().mode !== persisted) {
        set({ mode: persisted });
      }
    },
  }),
);
